import {
  useCourse,
  useEditCourse,
  useGetAdminCourseInsights,
  useGetAdminCoursesOverview,
  useSoftDeleteCourse,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import CourseCard from '@components/V3/CourseCard';
import CoursePictureEditor from '@components/V3/CoursePictureEditor';
import PreviewStudentProgressCard from '@components/V3/CoursePictureEditor/preview-student-progress-card';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Tabs from '@components/V4/Tabs/Tabs';
import {
  AcademicCapIcon,
  ArchiveIcon,
  ArrowLeftIcon,
  CollectionIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/outline';
import { MetricCard } from '@modules/Admin/Reporting/common';
import * as ROUTES from '@routes';
import { Course } from '@shared/common';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CourseAssessment from './CourseAssessment';
import CourseForm, { CourseFormValues, EMPTY_COURSE_FORM } from './CourseForm';
import CourseMentors from './CourseMentors';
import LmsContentPanel from './LmsContentPanel';
import { CourseSwatch, FlagPill, LmsStatusBadge } from './courseUi';

type TabValue = 'settings' | 'lms' | 'students' | 'images' | 'legacy';

const toFormValues = (course: Course): CourseFormValues => ({
  title: course.title ?? '',
  subtitle: course.subtitle ?? '',
  abbreviation: course.abbreviation ?? '',
  lmsCourseId: course.lmsCourseId ?? '',
  lmsCoursePrice: course.lmsCoursePrice ?? 0,
  isFree: !!course.isFree,
  isMentorless: !!course.isMentorless,
  isPublic: !!course.isPublic,
  country: course.country ?? '',
  color: course.color || EMPTY_COURSE_FORM.color,
  price: course.price ?? 0,
  youtubeId: course.youtubeId ?? '',
  description: course.description ?? '',
  details: course.details ?? '',
});

const diffValues = (current: CourseFormValues, original: CourseFormValues) =>
  (Object.keys(current) as (keyof CourseFormValues)[]).reduce<Partial<CourseFormValues>>(
    (changes, key) => {
      if (current[key] !== original[key]) {
        return { ...changes, [key]: current[key] };
      }
      return changes;
    },
    {}
  );

type LegacyLesson = {
  refType?: 'Lesson' | 'Quiz';
  _id: string | { _id?: string; title?: string };
  title?: string;
};

const legacyLessonTitle = (lesson: LegacyLesson) =>
  lesson.title ?? (typeof lesson._id === 'object' ? lesson._id?.title : undefined) ?? 'Untitled';

const legacyLessonKey = (lesson: LegacyLesson, index: number) =>
  typeof lesson._id === 'object' ? lesson._id?._id ?? String(index) : lesson._id ?? String(index);

const CourseDetails = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const toast = useToast();

  const { data: course, isLoading, refetch: refetchCourse } = useCourse(id);
  const {
    data: insights,
    isLoading: isInsightsLoading,
    refetch: refetchInsights,
  } = useGetAdminCourseInsights(id);
  const { data: overview, isFetching: isCatalogLoading } = useGetAdminCoursesOverview(
    { refresh: false },
    { staleTime: 5 * 60 * 1000 }
  );

  const [values, setValues] = useState<CourseFormValues>(EMPTY_COURSE_FORM);
  const [tab, setTab] = useState<TabValue>('settings');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    if (course) setValues(toFormValues(course));
  }, [course]);

  const original = useMemo(() => (course ? toFormValues(course) : EMPTY_COURSE_FORM), [course]);
  const changes = useMemo(() => diffValues(values, original), [values, original]);
  const isDirty = Object.keys(changes).length > 0;

  const { mutate: editCourse, isLoading: isSaving } = useEditCourse({
    onSuccess: () => {
      toast.success('Course updated.');
      refetchCourse();
      refetchInsights();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'The course could not be saved.');
    },
  });

  const { mutate: deleteCourse, isLoading: isDeleting } = useSoftDeleteCourse(id, {
    onSuccess: () => {
      toast.success('Course deleted.');
      history.push(ROUTES.MANAGE_COURSES);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'The course could not be deleted.');
    },
  });

  const save = () => {
    if (!values.title.trim()) {
      toast.error('A title is required.');
      return;
    }
    editCourse({ id, changes });
  };

  if (isLoading || !course) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const legacyModules = (course.modules ?? []) as unknown as {
    _id: string;
    title: string;
    subtitle?: string;
    isSkippable?: boolean;
    resources?: { text: string; url: string }[];
    lessons: LegacyLesson[];
  }[];
  const legacyCount = legacyModules.reduce((sum, module) => sum + (module.lessons?.length ?? 0), 0);

  const tabs: { label: string; value: TabValue }[] = [
    { label: 'Settings', value: 'settings' },
    { label: 'LMS content', value: 'lms' },
    { label: 'Students & mentors', value: 'students' },
    { label: 'Images', value: 'images' },
    ...(legacyModules.length
      ? [{ label: `Legacy content (${legacyCount})`, value: 'legacy' as TabValue }]
      : []),
  ];

  const lmsStatus = insights?.lmsStatus ?? 'unlinked';

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <button
        type="button"
        className="inline-flex w-fit items-center gap-2 text-sm text-customGrey hover:text-white"
        onClick={() => history.push(ROUTES.MANAGE_COURSES)}
      >
        <ArrowLeftIcon className="h-4 w-4" /> All courses
      </button>

      <div className="flex flex-col gap-4 laptop:flex-row laptop:items-start laptop:justify-between">
        <div className="flex items-start gap-4">
          <CourseSwatch color={values.color} abbreviation={values.abbreviation} size="large" />
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold">{course.title}</h1>
              {course.subtitle ? (
                <span className="text-sm text-customGrey">{course.subtitle}</span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <FlagPill>
                {course.isFree ? 'Free' : course.price ? `${course.price} €` : 'Paid'}
              </FlagPill>
              {course.isMentorless ? <FlagPill tone="warn">Mentorless</FlagPill> : null}
              {course.country ? <FlagPill>{course.country}</FlagPill> : null}
              {!isInsightsLoading ? <LmsStatusBadge status={lmsStatus} /> : null}
              {legacyModules.length ? (
                <FlagPill tone="warn">Has legacy Agora lessons</FlagPill>
              ) : null}
            </div>
            <span className="text-xs text-customGrey">
              Created {moment(course.createdAt).format('D MMM YYYY')} · last updated{' '}
              {moment(course.updatedAt).fromNow()}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary-red"
            buttonText="Delete course"
            onClick={() => setIsDeleteOpen(true)}
          />
          <Button
            buttonText={isDirty ? 'Save changes' : 'Saved'}
            disabled={!isDirty}
            isLoading={isSaving}
            onClick={save}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          label="Active students"
          value={insights?.enrollments.active ?? '—'}
          icon={<AcademicCapIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Enrollments all time"
          value={insights?.enrollments.total ?? '—'}
          icon={<UsersIcon className="h-5 w-5" />}
          accent="lila"
        />
        <MetricCard
          label="Mentors who taught it"
          value={insights?.enrollments.mentors ?? '—'}
          icon={<UserGroupIcon className="h-5 w-5" />}
          accent="gold"
        />
        <MetricCard
          label="LMS learning units"
          value={insights?.lms ? insights.lms.unitCount : '—'}
          icon={<CollectionIcon className="h-5 w-5" />}
          accent={lmsStatus === 'linked' ? 'green' : 'gold'}
          onClick={() => setTab('lms')}
        />
      </div>

      <Tabs<TabValue>
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
        renderContent={(current) => {
          if (current === 'settings') {
            return (
              <CourseForm
                mode="edit"
                value={values}
                onChange={(patch) => setValues((state) => ({ ...state, ...patch }))}
                lmsCatalog={overview?.lmsCatalog ?? []}
                isLmsCatalogLoading={isCatalogLoading}
              />
            );
          }

          if (current === 'lms') {
            return (
              <LmsContentPanel
                status={lmsStatus}
                lms={insights?.lms ?? null}
                lmsCourseId={course.lmsCourseId}
                error={insights?.lmsError}
                isLoading={isInsightsLoading}
                pendingLmsCourseId={values.lmsCourseId}
              />
            );
          }

          if (current === 'students') {
            return (
              <div className="flex flex-col gap-8">
                <CourseMentors mentors={insights?.mentors} isLoading={isInsightsLoading} />
                <CourseAssessment courseId={id} />
              </div>
            );
          }

          if (current === 'images') {
            return (
              <div className="grid grid-cols-1 gap-6 laptop:grid-cols-2">
                <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
                  <div className="flex flex-col gap-0.5">
                    <h3 className="text-base font-semibold">Course image</h3>
                    <p className="text-xs text-customGrey">
                      Square crop is used on cards, the wide crop on the student progress card.
                    </p>
                  </div>
                  <CoursePictureEditor
                    id={id}
                    refetchData={() => refetchCourse()}
                    handleImageUploaded={() => undefined}
                    title={course.title}
                  />
                </div>
                <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
                  <h3 className="text-base font-semibold">Preview</h3>
                  <div className="max-w-sm">
                    <CourseCard
                      title={values.title}
                      image={course.headerImage?.['160x160'] ?? null}
                    />
                  </div>
                  <div className="max-w-sm">
                    <PreviewStudentProgressCard
                      courseTitle={values.title}
                      image={course.headerImage?.['470x320'] ?? null}
                    />
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3 rounded-2xl bg-customGold/10 p-4 text-sm text-customGold">
                <ArchiveIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>
                  These modules and lessons were authored inside Agora before course content moved
                  to the LMS. They are no longer delivered to students and are kept read-only for
                  reference.
                </span>
              </div>
              {legacyModules.map((module, moduleIndex) => (
                <div
                  key={module._id ?? moduleIndex}
                  className="flex flex-col rounded-2xl bg-surfaceObject"
                >
                  <div className="flex flex-col gap-0.5 px-5 py-3">
                    <span className="text-sm font-semibold text-white">
                      <span className="mr-2 text-customGrey">{moduleIndex + 1}.</span>
                      {module.title || 'Untitled module'}
                    </span>
                    {module.subtitle ? (
                      <span className="text-xs text-customGrey">{module.subtitle}</span>
                    ) : null}
                    {module.resources?.length ? (
                      <span className="text-xs text-customGrey">
                        {module.resources.length} resource link
                        {module.resources.length === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </div>
                  {module.lessons?.length ? (
                    <ul className="flex flex-col border-t border-white/5">
                      {module.lessons.map((lesson, lessonIndex) => (
                        <li
                          key={legacyLessonKey(lesson, lessonIndex)}
                          className="flex items-center gap-3 border-b border-white/5 px-5 py-2 text-sm last:border-b-0"
                        >
                          <span className="min-w-0 flex-1 truncate text-customLight">
                            {legacyLessonTitle(lesson)}
                          </span>
                          <span
                            className={mergeClassNames(
                              'shrink-0 rounded-full px-2 py-0.5 text-xs',
                              lesson.refType === 'Quiz'
                                ? 'bg-customLila/15 text-customLila'
                                : 'bg-white/10 text-customGrey'
                            )}
                          >
                            {lesson.refType === 'Quiz' ? 'Quiz' : 'Lesson'} · legacy
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="border-t border-white/5 px-5 py-3 text-xs text-customGrey">
                      No lessons in this module.
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        }}
      />

      <Modal size="small" isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
        <Modal.Header title="Delete this course?" />
        <Modal.Body>
          <p className="text-sm text-customGrey">
            The course is hidden everywhere in Agora. Existing enrollments keep their data and the
            LMS course is not touched.
          </p>
        </Modal.Body>
        <Modal.Footer className="mt-4">
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              buttonText="Keep it"
              onClick={() => setIsDeleteOpen(false)}
            />
            <Button
              variant="primary-error"
              buttonText="Delete course"
              isLoading={isDeleting}
              onClick={() => deleteCourse({})}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseDetails;
